// Quotations data layer (Phase 4) — header + line items.
//
// The SINGLE interface between the app and the quotation schema
// (supabase/migrations/20260630213320_quotations.sql).
//
// SECURITY MODEL (server-enforced; mirrored here):
//   • We NEVER send company_id or quotation_number — DB triggers own them (company
//     forced/inherited; quotation_number auto = QT-00001; both immutable). RLS scopes all.
//
// THE KEY FEATURE — one-click conversion to a Sales Order:
//   • convertToSalesOrder copies customer / lines / pricing / VAT / notes into a new
//     Sales Order (which then reserves stock via its own sync), links both documents
//     (converted_sales_order_id here, reference = QT number there) and marks the
//     quotation 'converted'. No re-entry. Quotations themselves never touch inventory.
//
// RETURN-TYPE CONTRACT (consistent with salesOrders):
//   collection → { data, count, page?, pageSize? } · single → T|null ·
//   create/update/duplicate → id · convert → { salesOrderId, salesOrderNumber } ·
//   delete → void · all THROW a mapped Error.

import { supabase } from "./supabaseClient";
import { createSalesOrder, getSalesOrder } from "./salesOrders";
import { todayISO } from "./dates";
import { C } from "../customers/theme";

const Q = "quotations";
const LINES = "quotation_lines";

export const DEFAULT_PAGE_SIZE = 25;

/** Colour-coded statuses (+ DB CHECK parity). */
export const QT_STATUSES = [
  { value: "draft", label: "Draft", color: C.textMuted },
  { value: "sent", label: "Sent", color: C.primary },
  { value: "accepted", label: "Accepted", color: C.success },
  { value: "rejected", label: "Rejected", color: C.danger },
  { value: "expired", label: "Expired", color: "#b45309" },
  { value: "converted", label: "Converted", color: C.purple },
];

export const CURRENCIES = [
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
  { value: "USD", label: "USD ($)" },
];

export const QT_SORTS = [
  { value: "number", label: "Quote No." },
  { value: "issue_date", label: "Issue Date" },
  { value: "expiry_date", label: "Expiry Date" },
  { value: "status", label: "Status" },
  { value: "total", label: "Total" },
  { value: "created", label: "Newest" },
];

const HEADER_WRITE_FIELDS = ["customer_id", "customer_name", "issue_date", "expiry_date", "salesperson", "reference", "status", "preferred_currency", "notes"];
const LINE_WRITE_FIELDS = ["product_id", "sku", "description", "quantity", "unit_price", "discount_percent", "vat_rate"];
const SORT_COLUMNS = { number: "quotation_number", issue_date: "issue_date", expiry_date: "expiry_date", status: "status", total: "total_amount", created: "created_at" };

const HEADER_SELECT =
  "id, quotation_number, customer_id, customer_name, issue_date, expiry_date, salesperson, reference, " +
  "status, preferred_currency, subtotal, total_discount, total_vat, total_amount, converted_sales_order_id, converted_sales_order_number, created_at, updated_at, " +
  "customers ( id, company_name )";
const FULL_SELECT =
  "*, customers ( id, company_name, payment_terms, preferred_currency ), " +
  "quotation_lines ( id, product_id, sku, description, quantity, unit_price, discount_percent, vat_rate, line_total, position )";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const pick = (obj, fields) => {
  const out = {};
  for (const f of fields) if (obj && obj[f] !== undefined) out[f] = obj[f] === "" ? null : obj[f];
  return out;
};
const sanitizeTerm = (t) => String(t || "").trim().replace(/[%,()]/g, "");
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "23514": message = "Invalid value — check the status field."; break;
    case "23503": message = "Referenced customer or product does not exist."; break;
    case "23502": message = "A required field is missing."; break;
    case "PGRST116": message = "Quotation not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// ── Totals (form preview AND on save) ────────────────────────────────────────

const lineNet = (l) => (Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100);

export function computeTotals(lines = []) {
  let subtotal = 0, total_discount = 0, total_vat = 0;
  const computed = lines.map((l, i) => {
    const net = lineNet(l);
    const gross = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
    subtotal += net;
    total_discount += gross - net;
    total_vat += net * ((Number(l.vat_rate) || 0) / 100);
    return { ...l, line_total: round2(net), position: i };
  });
  return {
    lines: computed,
    subtotal: round2(subtotal),
    total_discount: round2(total_discount),
    total_vat: round2(total_vat),
    total_amount: round2(subtotal + total_vat),
  };
}

// ── Reads ────────────────────────────────────────────────────────────────────

/**
 * List quotations (RLS-scoped) with search, status/customer filters, sort, pagination.
 * @param {Object} [opts]
 * @returns {Promise<{data:Array,count:number,page:number,pageSize:number}>}
 */
export async function getQuotations(opts = {}) {
  const { search, status, customerId, page = 1, pageSize = DEFAULT_PAGE_SIZE, sortBy = "created", sortDir = "desc" } = opts;
  const c = client();
  const column = SORT_COLUMNS[sortBy] || "created_at";
  const safePage = Math.max(1, Number(page) || 1);
  const safeSize = Math.max(1, Number(pageSize) || DEFAULT_PAGE_SIZE);
  const from = (safePage - 1) * safeSize;
  let q = c.from(Q).select(HEADER_SELECT, { count: "exact" }).order(column, { ascending: sortDir !== "desc" }).range(from, from + safeSize - 1);
  if (status && status !== "all") q = q.eq("status", status);
  if (customerId && customerId !== "all") q = q.eq("customer_id", customerId);
  const term = sanitizeTerm(search);
  if (term) q = q.or(`quotation_number.ilike.%${term}%,customer_name.ilike.%${term}%,reference.ilike.%${term}%,salesperson.ilike.%${term}%`);
  const { data, error, count } = await q;
  if (error) throw toError(error, "Could not load quotations");
  return { data: data || [], count: count ?? 0, page: safePage, pageSize: safeSize };
}

export async function searchQuotations(term, opts = {}) { return getQuotations({ ...opts, search: term }); }

/** Full quotation (header + customer + lines, ordered). */
export async function getQuotation(id) {
  const { data, error } = await client().from(Q).select(FULL_SELECT).eq("id", id).maybeSingle();
  if (error) throw toError(error, "Could not load quotation");
  if (data?.quotation_lines) data.quotation_lines.sort((a, b) => (a.position || 0) - (b.position || 0));
  return data || null;
}

// ── Writes ───────────────────────────────────────────────────────────────────

async function replaceLines(qId, lines) {
  const c = client();
  const { error: delErr } = await c.from(LINES).delete().eq("quotation_id", qId);
  if (delErr) throw toError(delErr, "Could not update line items");
  const rows = (lines || [])
    .filter((l) => l.product_id || (l.description && l.description.trim()) || Number(l.quantity) > 0)
    .map((l, i) => ({ ...pick(l, LINE_WRITE_FIELDS), line_total: round2(lineNet(l)), position: i, quotation_id: qId }));
  if (rows.length) {
    const { error } = await c.from(LINES).insert(rows);
    if (error) throw toError(error, "Could not save line items");
  }
}

/**
 * Create a quotation with its lines.
 * @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} new quotation id.
 */
export async function createQuotation(payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const headerRow = {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat, total_amount: totals.total_amount,
  };
  const { data, error } = await c.from(Q).insert(headerRow).select("id").single();
  if (error) throw toError(error, "Could not create quotation");
  await replaceLines(data.id, totals.lines);
  return data.id;
}

/**
 * Update a quotation header + replace its lines.
 * @param {string} id @param {Object} payload Header fields + `lines: Array`.
 * @returns {Promise<string>} the quotation id.
 */
export async function updateQuotation(id, payload) {
  const c = client();
  const totals = computeTotals(payload?.lines || []);
  const headerRow = {
    ...pick(payload || {}, HEADER_WRITE_FIELDS),
    subtotal: totals.subtotal, total_discount: totals.total_discount, total_vat: totals.total_vat, total_amount: totals.total_amount,
  };
  const { error } = await c.from(Q).update(headerRow).eq("id", id).select("id").single();
  if (error) throw toError(error, "Could not update quotation");
  await replaceLines(id, totals.lines);
  return id;
}

/** Delete a quotation (cascades its lines). */
export async function deleteQuotation(id) {
  const { error } = await client().from(Q).delete().eq("id", id);
  if (error) throw toError(error, "Could not delete quotation");
}

/**
 * Duplicate a quotation as a fresh DRAFT (new number, today's issue date) — same
 * customer, lines and pricing. A common SME workflow (re-quote / similar job).
 * @param {string} id @returns {Promise<string>} new quotation id.
 */
export async function duplicateQuotation(id) {
  const q = await getQuotation(id);
  if (!q) throw toError({ code: "PGRST116" }, "Quotation not found");
  const lines = (q.quotation_lines || []).map((l) => pick(l, LINE_WRITE_FIELDS));
  return createQuotation({
    customer_id: q.customer_id, customer_name: q.customer_name,
    issue_date: todayISO(), expiry_date: q.expiry_date, salesperson: q.salesperson,
    reference: q.reference, status: "draft", preferred_currency: q.preferred_currency, notes: q.notes, lines,
  });
}

/**
 * THE KEY FEATURE — convert a quotation into a Sales Order in one click.
 * Copies customer, lines, pricing, VAT and notes; links both documents and marks the
 * quotation 'converted'. The created Sales Order reserves stock via its own sync.
 * @param {string} id
 * @returns {Promise<{salesOrderId:string, salesOrderNumber:string}>}
 */
export async function convertToSalesOrder(id) {
  const c = client();
  const q = await getQuotation(id);
  if (!q) throw toError({ code: "PGRST116" }, "Quotation not found");
  if (q.status === "converted" && q.converted_sales_order_id) {
    const e = new Error(`This quotation was already converted to ${q.converted_sales_order_number || "a sales order"}.`);
    e.code = "ALREADY_CONVERTED"; throw e;
  }
  const lines = (q.quotation_lines || []).map((l) => pick(l, LINE_WRITE_FIELDS));
  const soId = await createSalesOrder({
    customer_id: q.customer_id, customer_name: q.customer_name,
    order_date: todayISO(), status: "confirmed", salesperson: q.salesperson,
    reference: q.quotation_number, preferred_currency: q.preferred_currency, notes: q.notes, lines,
  });
  const so = await getSalesOrder(soId);
  const { error } = await c.from(Q).update({ status: "converted", converted_sales_order_id: soId, converted_sales_order_number: so?.so_number || null }).eq("id", id);
  if (error) throw toError(error, "Sales order created, but the quotation could not be marked converted");
  return { salesOrderId: soId, salesOrderNumber: so?.so_number };
}
